import React, { FormEvent, useState, useRef, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
// import { useAuth } from '../../../../firebase/AuthProvider';


// styles for create plan modal
import './plan_createplan.css';

import { CreatePlanAsync } from '../../../../../services/PlanService/PlanService';
import { useAuth } from '../../../../../firebase/AuthProvider';
import { CreatePlanRequest } from '../../../../../interfaces';
import { BillingInterval } from '../../../../../Data/SD';

interface CreatePlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const { currentUser } = useAuth();
    const [form, setForm] = useState<CreatePlanRequest>({
        name: '',
        basePriceCents: 0,
        seatPriceCents: 0,
        billingInterval: BillingInterval.MONTHLY,
    });
    const [errors, setErrors] = useState<Partial<Record<keyof CreatePlanRequest, string>>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // abort controller so the request can be cancelled if the modal closes/unmounts
    const controllerRef = useRef<AbortController | null>(null);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
        };
    }, []);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'number'
                    ? Number(value)
                    : value,
        } as any));
        // clear error for this field if present
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof CreatePlanRequest, string>> = {};
        if (!form.name.trim()) {
            newErrors.name = 'Please enter a plan name.';
        }
        if (form.basePriceCents === null || form.basePriceCents === undefined || form.basePriceCents < 0) {
            newErrors.basePriceCents = 'Please enter a base price in cents.';
        }
        if (form.seatPriceCents === null || form.seatPriceCents === undefined || form.seatPriceCents < 0) {
            newErrors.seatPriceCents = 'Please enter a seat price in cents.';
        }
        if (!form.billingInterval) {
            newErrors.billingInterval = 'Please choose a billing interval.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        if (!token) {
            toast.error('Authentication error');
            return;
        }

        setIsLoading(true);
        // create and store abort controller for this request
        const controller = new AbortController();
        controllerRef.current = controller;

        const payload: CreatePlanRequest = {
            name: form.name.trim(),
            basePriceCents: form.basePriceCents,
            seatPriceCents: form.seatPriceCents,
            billingInterval: form.billingInterval as string,
        };

        try {
            const res = await CreatePlanAsync(token, payload, controller.signal);
            if (res.success) {
                toast.success('Plan created successfully');
                onSuccess && onSuccess();
                onClose();
            } else {
                toast.error(res.message);
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                // request was cancelled; silently ignore or show optional message
            } else {
                toast.error(err.message || 'Unable to create plan');
            }
        } finally {
            setIsLoading(false);
            controllerRef.current = null;
        }
    };

    const handleClose = () => {
        // abort any in-flight request
        controllerRef.current?.abort();
        controllerRef.current = null;

        setErrors({});
        setForm({
            name: '',
            basePriceCents: 0,
            seatPriceCents: 0,
            billingInterval: BillingInterval.MONTHLY,
        });
        onClose();
    };

    return (
        <>
            <Modal
                show={isOpen}
                onHide={handleClose}
                className="custom_popup"
                centered
                backdrop={isLoading ? 'static' : true}
                keyboard={!isLoading}
            >
                <form onSubmit={handleSubmit}>
                    <Modal.Header closeButton={!isLoading}>
                        <Modal.Title>Create New Plan</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="plan_formGroup">
                            <label className="plan_label" htmlFor="name">
                                Plan Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                value={form.name}
                                onChange={handleInputChange}
                                className="plan_input"
                            />
                            {errors.name && (
                                <div className="text-red-500 text-sm">
                                    {errors.name}
                                </div>
                            )}
                        </div>

                        <div className="plan_formGroup">
                            <label className="plan_label" htmlFor="basePriceCents">
                                Base Price (cents)
                            </label>
                            <input
                                id="basePriceCents"
                                name="basePriceCents"
                                type="number"
                                min="0"
                                value={form.basePriceCents}
                                onChange={handleInputChange}
                                className="plan_input"
                            />
                            {errors.basePriceCents && (
                                <div className="text-red-500 text-sm">
                                    {errors.basePriceCents}
                                </div>
                            )}
                        </div>

                        <div className="plan_formGroup">
                            <label className="plan_label" htmlFor="seatPriceCents">
                                Seat Price (cents)
                            </label>
                            <input
                                id="seatPriceCents"
                                name="seatPriceCents"
                                type="number"
                                min="0"
                                value={form.seatPriceCents}
                                onChange={handleInputChange}
                                className="plan_input"
                            />
                            {errors.seatPriceCents && (
                                <div className="text-red-500 text-sm">
                                    {errors.seatPriceCents}
                                </div>
                            )}
                        </div>

                        <div className="plan_formGroup">
                            <label className="plan_label" htmlFor="billingInterval">
                                Billing Interval
                            </label>
                            <select
                                id="billingInterval"
                                name="billingInterval"
                                value={form.billingInterval}
                                onChange={handleInputChange}
                                className="plan_select"
                            >
                                <option value={BillingInterval.MONTHLY}>Monthly</option>
                                <option value={BillingInterval.ANNUAL}>ANNUAL</option>
                                <option value={BillingInterval.QUATERLY}>QUATERLY</option>
                            </select>
                            {errors.billingInterval && (
                                <div className="text-red-500 text-sm">
                                    {errors.billingInterval}
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <Button
                            type="submit"
                            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-[#7E57C2] text-white hover:bg-[#6D4CAB] flex items-center justify-center"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="mr-2"
                                />
                            ) : "Create Plan"
                            }

                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>
        </>
    );
};

export default CreatePlanModal;
